import axios from "axios";
import { setSelectedInboxTab } from "../redux/Slice/Inbox/inboxSlice";
import { getAuthToken } from "./getAuthToken";

const BASE_URL = `${process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000"}/api`;
const LOOKUP_TIMEOUT_MS = 4000;

/**
 * Which Inbox sidebar tab a contact lives under, from a fresh read of the contact itself:
 * "Blocked Contacts" | "Closed" | "All". Returns null when it can't be determined (offline, slow,
 * 404, no token) -- callers then leave the tab alone, i.e. the previous behavior.
 *
 * Deliberately not fetchContactById: that thunk serves a cached copy (up to 1h in IndexedDB), and a
 * stale Closed/Open status here would put the user on the wrong tab.
 */
export async function resolveInboxTabForContact(contactId) {
  if (!contactId) return null;
  try {
    const token = await getAuthToken();
    if (!token) return null;
    const { data } = await axios.get(`${BASE_URL}/contacts/getbyid/${contactId}`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: LOOKUP_TIMEOUT_MS,
    });
    if (data?.isBlocked) return "Blocked Contacts";
    if (data?.status === "Closed") return "Closed";
    return "All";
  } catch (e) {
    return null;
  }
}

/**
 * Open a contact's chat in the Inbox from a page OUTSIDE the Inbox (Reports, Contacts, ...), telling
 * the Inbox up front which sidebar tab the contact lives under (Closed / Blocked Contacts).
 *
 * The tab is passed as router state (`inboxTab`) rather than set in Redux before navigating:
 * mounting the Inbox already on Closed changed the order in which the chat-open flow and the
 * Closed list load, and that raced. MessageCard applies the hint through its existing deep-link
 * tab correction (same skip-flag + tab switch used for any Closed/Blocked deep link) as soon as the
 * chat selection lands, without having to wait for the lists / contact info to discover the status.
 * If the lookup fails or times out it navigates exactly as before and MessageCard's own detection
 * still corrects the tab. Open contacts get no hint (they live in All and the other tabs).
 */
export async function openContactInInbox({ dispatch, navigate, accountid, userid, contactId }) {
  if (!contactId || !accountid || !userid) return;
  const tab = await resolveInboxTabForContact(contactId);
  const url = `/home/${accountid}/inbox/${userid}/chat/${contactId}`;
  if (tab === "Closed" || tab === "Blocked Contacts") {
    // Also set the tab now (the Inbox isn't mounted, so no tab-change effect can fire) so it mounts
    // already on it -- no flash of "All". The router-state hint below is the backup that corrects
    // the tab if this didn't take (e.g. state reset by a refresh).
    dispatch?.(setSelectedInboxTab(tab));
    navigate(url, { state: { inboxTab: tab } });
  } else {
    navigate(url);
  }
}
